import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { IonModal } from '@ionic/angular';
import {
  DEFAULT_DURATION, DEFAULT_VOLUME, MOOD_DURATIONS, MOOD_VOLUME_MAX, MOOD_VOLUME_MIN,
  MOOD_VOLUME_STEP, MoodDuration, MoodSound, clampVolume, deriveName, durationLabel,
} from '../../consts/mood-audio';

/**
 * Two-column wheel — Sound | Duration — in the app's standard picker sheet.
 * Cloned from temperature-picker; the sheet, wheel and Set button styling all
 * come from theme/_wheel-picker.scss so this cannot drift from its siblings.
 *
 * Used in two places: the scene editor (with the tappable panel face) and the
 * Scenes-widget transport bar (panel="false", opened by the parent via open()).
 */
@Component({
  selector: 'sound-picker',
  templateUrl: './sound-picker.component.html',
  styleUrls: ['./sound-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoundPickerComponent implements OnChanges {
  @ViewChild(IonModal) modal!: IonModal;

  @Input() title = '';
  /** The unit's resolved library. */
  @Input() sounds: MoodSound[] = [];
  /** Currently selected file stem, or null. */
  @Input() sound: string | null = null;
  @Input() duration: MoodDuration = DEFAULT_DURATION;
  /** False renders the modal only — the parent presents it via open(). */
  @Input() panel = true;
  @Input() disabled = false;
  /** Mood audio's remembered level, 0-100. */
  @Input() volume: number = DEFAULT_VOLUME;
  /**
   * Render the volume slider. OFF by default because the scene editor shares
   * this picker and a scene carries no volume — the scene engine never passes one
   * to mood_audio_start, so a slider there would be a control that does nothing.
   */
  @Input() showVolume = false;

  /**
   * Set commits the staged wheels AND the slider's level. Hosts that apply volume
   * live (the transport bar) ignore the volume here and bind onVolumeChanged
   * instead; hosts that want a snapshot (the scene editor) bind only this.
   */
  @Output() onSelected =
    new EventEmitter<{ sound: string; duration: MoodDuration; volume: number }>();
  /**
   * Fired on knob RELEASE, not on every drag frame — the same shape the home
   * Dimmer uses. Volume is the one control in this sheet that is live rather
   * than staged behind Set, so the resident hears the level they are choosing.
   */
  @Output() onVolumeChanged = new EventEmitter<number>();

  readonly durations = MOOD_DURATIONS;
  readonly durationLabel = durationLabel;
  readonly volumeMin = MOOD_VOLUME_MIN;
  readonly volumeMax = MOOD_VOLUME_MAX;
  readonly volumeStep = MOOD_VOLUME_STEP;

  /** Wheel selections inside the modal, discarded unless Set is pressed. */
  selectedSound: string | null = null;
  selectedDuration: MoodDuration = DEFAULT_DURATION;

  /** Slider position. Unlike the wheels this is NOT discarded — it is live. */
  sliderVolume: number = DEFAULT_VOLUME;

  /**
   * True between knob-down and knob-up. While it is set, incoming values from
   * the unit are ignored: getMessageListener re-emits the whole device-state map
   * on unrelated traffic, and the amplifier republishes every few seconds while
   * playing, so an echo landing mid-drag would jerk the knob out from under the
   * resident's finger.
   */
  private dragging = false;


  /**
   * This component is OnPush and the host mutates it directly through a ViewChild
   * (open, revertVolume). The host's own markForCheck marks the BAR — this is a
   * child, so it is not re-checked by that, and the slider would keep rendering a
   * stale level. Hence a local ref.
   */
  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Reseed the wheel ONLY when the selection inputs actually changed. `sounds` is
   * a fresh array on every shadow re-emission (the listener republishes the whole
   * device-state map), and while audio plays the amplifier republishes every few
   * seconds — so reseeding on any change would throw away a resident's
   * in-progress scroll mid-selection. open() realigns the wheel to the inputs
   * anyway, so reseeding on unrelated churn buys nothing.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sound'] || changes['duration']) {
      this.selectedSound = this.resolvedSound;
      this.selectedDuration = this.duration;
    }
    // Deliberately its own branch: a volume echo must not reseed the WHEELS and
    // throw away a scroll the resident has not applied yet.
    if (changes['volume'] && !this.dragging) {
      this.sliderVolume = clampVolume(this.volume);
    }
  }

  /**
   * Exactly what the unit can play, and nothing else.
   *
   * This used to append a stem the unit no longer has, so a scene referencing a
   * removed sound "stayed selectable and removable". In practice the only thing a
   * resident could do with that row was choose a sound that cannot play — seen on
   * the dev unit as a wheel entry reading "This stem does not exist". The library
   * is folder-driven, so its contents are precisely what will play; anything
   * outside it is not an option in any useful sense.
   *
   * A scene carrying a removed stem now lands on an available sound instead — see
   * resolvedSound — so pressing Set always commits something that works.
   */
  get displaySounds(): MoodSound[] {
    return this.sounds;
  }

  get faceLabel(): string {
    const id = this.resolvedSound;
    if (!id) { return 'Choose a sound'; }
    const name = this.displaySounds.find((s) => s.id === id)?.name ?? deriveName(id);
    return `${name} · ${durationLabel(this.duration)}`;
  }

  private get resolvedSound(): string | null {
    const playable = !!this.sound && this.sounds.some((s) => s.id === this.sound);
    return playable ? this.sound : (this.sounds[0]?.id ?? null);
  }

  onVolumeKnobStart(): void {
    this.dragging = true;
  }


  onVolumeKnobEnd(event: CustomEvent): void {
    this.dragging = false;
    const level = clampVolume(Number(event.detail.value));
    this.sliderVolume = level;
    // ion-range fires this for a bare tap and for every arrow keypress, not just for
    // a real drag, so an unchanged level would still cost a script call, an amplifier
    // write, a shadow republish and an analytics event. The Night Light slider guards
    // the same way.
    if (level === clampVolume(this.volume)) { return; }
    this.onVolumeChanged.emit(level);
  }

  /**
   * Put the slider back where the unit has it. Called by the host when a write
   * is rejected — without it the slider would keep showing a level the unit
   * never accepted, correctable only by some unrelated future publish.
   */
  revertVolume(): void {
    this.dragging = false;
    this.sliderVolume = clampVolume(this.volume);
    this.cdr.markForCheck();
  }

  onSoundChange(event: CustomEvent): void {
    this.selectedSound = String(event.detail.value);
  }

  onDurationChange(event: CustomEvent): void {
    this.selectedDuration = Number(event.detail.value) as MoodDuration;
  }

  open(): void {
    if (this.disabled) { return; }
    // Realign to the saved values so a scroll that was dismissed without Set is
    // discarded rather than re-offered — same behaviour as temperature-picker.
    this.selectedSound = this.resolvedSound;
    this.selectedDuration = this.duration;
    // Resync the slider ONLY after a drag the sheet was dismissed in the middle of.
    // Then, ionKnobMoveEnd never fired, so the slider holds a level that was never
    // sent anywhere and incoming echoes have been suppressed — it has to be brought
    // back to the unit's value, and `dragging` cleared or every later echo is dropped
    // too. Outside that case ngOnChanges already keeps the two in step, and resyncing
    // unconditionally would clobber a level committed moments ago whose echo is still
    // in flight.
    if (this.dragging) {
      this.sliderVolume = clampVolume(this.volume);
    }
    this.dragging = false;
    this.cdr.markForCheck();
    this.modal.present();
  }

  onApply(): void {
    if (this.selectedSound) {
      this.onSelected.emit({
        sound: this.selectedSound,
        duration: this.selectedDuration,
        volume: clampVolume(this.sliderVolume),
      });
    }
    this.modal.dismiss();
  }
}
